import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

const steps = ["Work Order Info", "Details & Files", "Review & Submit"];

const AddWorkOrderSmart = ({
  propertyId,
  openedByUserId = null,
  uploadedBy = null,
}) => {
  const getCurrentUserId = () => {
    const directId = Number(localStorage.getItem("currentUserId"));
    if (Number.isFinite(directId) && directId > 0) return directId;

    try {
      const storedUser = localStorage.getItem("user");
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      const parsedId = Number(parsed?.userId || parsed?.id);

      return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return null;
    }
  };

  const fallbackUserId = getCurrentUserId();
  const resolvedOpenedByUserId = openedByUserId || fallbackUserId;
  const resolvedUploadedBy = uploadedBy || fallbackUserId;

  console.log("✅ AddWorkOrderSmart openedByUserId prop:", openedByUserId);
  console.log("✅ AddWorkOrderSmart uploadedBy prop:", uploadedBy);
  console.log("✅ resolvedOpenedByUserId:", resolvedOpenedByUserId);
  console.log("✅ resolvedUploadedBy:", resolvedUploadedBy);

  const [activeStep, setActiveStep] = useState(0);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("work_order_draft");
    const parsedDraft = draft ? JSON.parse(draft) : {};

    return {
      description: parsedDraft.description || "",
      details: parsedDraft.details || "",
      property_id: propertyId || parsedDraft.property_id || "",
      workorder_type_id: parsedDraft.workorder_type_id || "",
      workorder_subtype_id: parsedDraft.workorder_subtype_id || "",
      location_id: parsedDraft.location_id || "",
    };
  });

  useEffect(() => {
    localStorage.setItem("work_order_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (!propertyId) return;
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId,
    }));
  }, [propertyId]);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!formData.property_id && !propertyId) return;

      try {
        const res = await api.get("/workorder-types/by-property", {
          params: { property_id: formData.property_id || propertyId },
        });

        const types = res.data?.types ?? [];
        setWorkOrderTypes(types);

        if (!types.length) {
          setSnackbar({
            open: true,
            severity: "info",
            message:
              "No work order types available for this property. Try another property or contact admin.",
          });
        }
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
        setWorkOrderTypes([]);
        setSnackbar({
          open: true,
          severity: "error",
          message:
            err.response?.data?.message ||
            "Could not load work order types for this property.",
        });
      }
    };

    fetchTypes();
  }, [formData.property_id, propertyId]);

  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchSubtypesAndLocations = async () => {
      try {
        const [subtypesRes, locationsRes] = await Promise.all([
          api.get("/workorder-subtypes-by-type", {
            params: { type_id: formData.workorder_type_id },
          }),
          api.get("/workorder-locations-by-type", {
            params: { type_id: formData.workorder_type_id },
          }),
        ]);

        setSubtypes(subtypesRes.data || []);
        setLocations(locationsRes.data || []);
      } catch (err) {
        console.error("Failed to fetch subtype/location data:", err);
        setSubtypes([]);
        setLocations([]);
      }
    };

    fetchSubtypesAndLocations();

    setFormData((prev) => ({
      ...prev,
      workorder_subtype_id: "",
      location_id: "",
    }));
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    setSelectedFiles((prev) => {
      const combined = [...prev, ...acceptedFiles];

      return combined.filter((file, index, self) => {
        return (
          index ===
          self.findIndex(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified
          )
        );
      });
    });
  }, []);

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
  });

  const selectedType = useMemo(
    () =>
      workOrderTypes.find(
        (t) => String(t.id) === String(formData.workorder_type_id)
      ),
    [workOrderTypes, formData.workorder_type_id]
  );

  const selectedSubtype = useMemo(
    () =>
      subtypes.find(
        (s) => String(s.id) === String(formData.workorder_subtype_id)
      ),
    [subtypes, formData.workorder_subtype_id]
  );

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.id) === String(formData.location_id)),
    [locations, formData.location_id]
  );

  const nextStep = () => setActiveStep((prev) => prev + 1);
  const prevStep = () => setActiveStep((prev) => prev - 1);

  const validateStep = () => {
    if (activeStep === 0) return !!formData.workorder_type_id;

    if (activeStep === 1) {
      return !!formData.description.trim() && !!formData.details.trim();
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const normalizedOpenedByUserId = Number(resolvedOpenedByUserId);
      const normalizedUploadedBy = Number(resolvedUploadedBy);

      const payloadJSON = {
        property_id: formData.property_id,
        description: formData.description.trim(),
        details: formData.details.trim(),
        workorder_type_id: formData.workorder_type_id,
        workorder_subtype_id: formData.workorder_subtype_id || null,
        location_id: formData.location_id || null,
        opened_by_user_id:
          Number.isFinite(normalizedOpenedByUserId) &&
          normalizedOpenedByUserId > 0
            ? normalizedOpenedByUserId
            : null,
      };

      console.log("📦 /addWorkOrder payload:", payloadJSON);

      const createRes = await api.post("/addWorkOrder", payloadJSON);
      const workorder_id = createRes.data?.workOrderId;

      if (!workorder_id) {
        throw new Error(
          `Missing workOrderId in response: ${JSON.stringify(createRes.data)}`
        );
      }

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadData = new FormData();

          uploadData.append("workorder_id", workorder_id);
          uploadData.append("upload_urls", file);

          if (
            Number.isFinite(normalizedUploadedBy) &&
            normalizedUploadedBy > 0
          ) {
            uploadData.append("uploaded_by", normalizedUploadedBy);
          }

          await api.post("/workorder-files", uploadData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      setFormData({
        description: "",
        details: "",
        property_id: propertyId || "",
        workorder_type_id: "",
        workorder_subtype_id: "",
        location_id: "",
      });

      setSelectedFiles([]);
      setActiveStep(0);
      localStorage.removeItem("work_order_draft");

      setSnackbar({
        open: true,
        message: "Work order created successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Submit error (AddWorkOrderSmart):", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    maxWidth: 980,
    mx: "auto",
    borderRadius: "24px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    overflow: "hidden",
  };

  const sectionCardSx = {
    borderRadius: "20px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
    backgroundColor: "#fff",
    p: { xs: 2, sm: 2.5, md: 3 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: "14px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f8fafc",
      borderRadius: "14px",
    },
  };

  const selectSx = {
    borderRadius: "14px",
    backgroundColor: "#fff",
    "& .MuiSelect-select": {
      py: 1.7,
    },
  };

  const primaryButtonSx = {
    background: "linear-gradient(135deg, #2f63ad 0%, #1f4f94 100%)",
    color: "#fff",
    fontWeight: 700,
    borderRadius: "12px",
    textTransform: "none",
    px: 3,
    minWidth: 150,
  };

  const secondaryButtonSx = {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.5,
    minWidth: 110,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    backgroundColor: "#fff",
  };

  const reviewItemSx = {
    p: 1.75,
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(248,250,252,0.85)",
    height: "100%",
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142", mb: 0.5 }}>
              Work Order Information
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Tooltip title="Select the type of work order">
                    <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                      Work Order Type
                    </InputLabel>
                  </Tooltip>
                  <Select
                    name="workorder_type_id"
                    value={formData.workorder_type_id}
                    onChange={handleFormChange}
                    required
                    sx={selectSx}
                  >
                    {workOrderTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!subtypes.length}>
                  <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                    Subtype
                  </InputLabel>
                  <Select
                    name="workorder_subtype_id"
                    value={formData.workorder_subtype_id}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {subtypes.map((sub) => (
                      <MenuItem key={sub.id} value={sub.id}>
                        {sub.subtype_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!locations.length}>
                  <InputLabel sx={{ backgroundColor: "#fff", px: 0.5 }}>
                    Location
                  </InputLabel>
                  <Select
                    name="location_id"
                    value={formData.location_id}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.location_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property ID"
                  name="property_id"
                  value={formData.property_id}
                  disabled
                  sx={readonlyFieldSx}
                />
              </Grid>
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142", mb: 0.5 }}>
              Details & Attachments
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  required
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Details"
                  name="details"
                  value={formData.details}
                  onChange={handleFormChange}
                  multiline
                  rows={5}
                  required
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed rgba(47,99,173,0.24)",
                    borderRadius: "18px",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 42, color: "#2f63ad", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                    {isDragActive ? "Drop files here" : "Drag & drop files or click to browse"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {selectedFiles.length > 0 ? (
                  selectedFiles.map((file, index) => (
                    <Box
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        display: "flex",
                        justifyContent: "space-between",
                        border: "1px solid rgba(47,99,173,0.08)",
                        borderRadius: "12px",
                        mb: 1,
                      }}
                    >
                      <Typography>{file.name}</Typography>
                      <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                    No files uploaded yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card elevation={0} sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>
                Review Your Work Order
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Type</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{selectedType?.type || "—"}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Subtype</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {selectedSubtype?.subtype_name || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Location</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {selectedLocation?.location_name || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={reviewItemSx}>
                  <HomeWorkOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Property ID</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{formData.property_id || "—"}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Description</Typography>
                  <Typography>{formData.description || "—"}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
                  <Typography variant="body2">Details</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {formData.details || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={reviewItemSx}>
                  <Typography variant="body2">Attached Files</Typography>
                  {selectedFiles.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                      {selectedFiles.map((file, idx) => (
                        <Chip
                          key={`${file.name}-${idx}`}
                          icon={<AttachFileIcon />}
                          label={file.name}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography>None</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Card sx={pageCardSx}>
      <input
        type="hidden"
        name="opened_by_user_id"
        value={resolvedOpenedByUserId ?? ""}
      />

      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid rgba(47,99,173,0.08)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
          <AssignmentIcon sx={{ color: "#2f63ad" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>
              Add Work Order
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ p: 3 }}>
        {renderStepContent()}

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: "1px solid rgba(47,99,173,0.08)",
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={prevStep}
            disabled={activeStep === 0 || isSubmitting}
            sx={secondaryButtonSx}
          >
            Back
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={nextStep}
              disabled={isSubmitting || !validateStep()}
              sx={primaryButtonSx}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={primaryButtonSx}
            >
              {isSubmitting ? <CircularProgress size={22} color="inherit" /> : "Submit Work Order"}
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AddWorkOrderSmart;